import React, { useState, useContext, useMemo, useEffect } from 'react'
import { ScreenContext } from '../..'

import { useLocation, useHistory } from "react-router-dom"

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Box,
    Chip,
    Tooltip,
    Divider
} from "@mui/material"

import {
    DataGridPro,
    GridToolbarContainer,
    GridToolbarColumnsButton,
    GridToolbarFilterButton,
    ptBR,
    getGridStringOperators,
    getGridBooleanOperators,
    getGridSingleSelectOperators,
    getGridDateOperators
} from '@mui/x-data-grid-pro';
import { ptBR as corePtBR } from '@mui/material/locale';
import { createTheme, ThemeProvider } from '@mui/material/styles';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CancelIcon from '@mui/icons-material/Cancel';
import HistoryIcon from '@mui/icons-material/History';
import MessageIcon from '@mui/icons-material/Message';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ReplayIcon from '@mui/icons-material/Replay';
import StopIcon from '@mui/icons-material/Stop';

import CampaignHistory from './CampaignHistory'
import ConfirmationDialog from '../../../Dialogs/Confirmation';
import CreateCampaignModal from '../../CreateCampaignModal'

import userPermissions from '../../..//User/Permissions';
import useNotification from '../../../Notification';
import { getSession } from '../../../../auth';
import api from '../../../../api';

import { reportTriggers, months } from "../../CreateCampaignModal"

import dayjs from "dayjs"
import "dayjs/locale/pt-br"
dayjs.locale('pt-br')

const userGroups = userPermissions();

const CampaignTable = () => {

    const location = useLocation()
    const history = useHistory()

    const {
        isSmallScreen,
        fetchNotifications,
        updateSelectedTab,
        updateStartDateTime,
        updateEndDateTime,
        groups,
        updateCampaigns,
        updateReady,
        fetchCampaigns,
        queues,
        originNumbers,
        templates,
        enterpriseId,
        campaigns,
        permissions,
        settings,
        rmvObj,
        ServerData,
        updateLimitOffset,
        limitOffset,
        filterModel,
        sortModel,
        updateFilterModel } = useContext(ScreenContext)

    const [campaignData, updateCampaignData] = useState([])
    const [showCampaignHistory, updateShowCampaignHistory] = useState(false)
    const [showCampaignDelete, updateShowCampaignDelete] = useState(false)
    const [showCampaignEdit, updateShowCampaignEdit] = useState(false)
    const [pageLimit, updatePageLimit] = useState(0)
    const [campaignsFilter, updateCampaignsFilter] = useState({ items: [] })
    const [columnVisibilityModel, setColumnVisibilityModel] = useState({ items: [] })
    const [campaignSortModel, updateCampaignSortModel] = useState([])
    const [duplicate, setDuplicate] = useState(null)
    const [voiceActionLoading, setVoiceActionLoading] = useState(null)

    useEffect(() => {
        updateCampaignsFilter(filterModel.campaigns)
        updateCampaignSortModel(sortModel.campaigns)
    }, [])

    useEffect(() => {
        setColumnVisibilityModel({
            sender: false,
            groups: !isSmallScreen,
            created_at: false,
            processed_at: !isSmallScreen,
            used: !isSmallScreen,
            template: false,
            queue_name: false,
            periodic: false,
            actions: !isSmallScreen,
            historic: !isSmallScreen,
            notifications: !isSmallScreen
        })
    }, [isSmallScreen])

    useEffect(() => {
        const hasActiveVoiceCampaign = campaigns.some((campaign) => (
            campaign.platform === 'VOICE' &&
            (
                ['STARTING', 'RUNNING', 'PAUSING', 'RESUMING', 'FINISHING'].includes(campaign?.voice_config?.ads_state) ||
                (campaign.active && ['', 'IDLE'].includes(campaign?.voice_config?.ads_state || 'IDLE'))
            )
        ))
        if (!hasActiveVoiceCampaign)
            return

        const currentLimit = (limitOffset.campaigns && limitOffset.campaigns[0]) || 25
        const currentOffset = (limitOffset.campaigns && limitOffset.campaigns[1]) || 0
        const interval = setInterval(() => {
            fetchCampaigns('auto', currentLimit, currentOffset)
        }, 5000)

        return () => clearInterval(interval)
    }, [campaigns, fetchCampaigns, limitOffset.campaigns])

    const handleHistory = (value) => {
        updateCampaignData(value)
        updateShowCampaignHistory(true)
    }

    const handleDelete = (value) => {
        updateCampaignData(value)
        updateShowCampaignDelete(true)
    }

    const handleEdit = (value) => {
        updateCampaignData(value)
        updateShowCampaignEdit(true)
    }

    const handleDuplicate = async (failedOnly = false) => {
        if (!duplicate?.id)
            return

        const response = await api.post(`/api/enterprises/${enterpriseId}/campaign/${duplicate.id}/duplicate/?failed_only=${failedOnly}`)
        if (response.status === 201) {
            setDuplicate(null)
            fetchCampaigns()
            useNotification(
                "Sucesso",
                "Campanha clonada com sucesso.",
                "success"
            )
            return
        }
        if (response.status === 400) {
            useNotification(
                "Atenção!",
                "Não há falhas que possam ser reenviadas.",
                "warning"
            )
            return
        }
        useNotification(
            "Ops!",
            "Algo deu errado ao copiar a campanha, tente novamente.",
            "danger"
        )
    }

    const handleVoiceLifecycle = async (campaign, action) => {
        const loadingKey = `${campaign.id}-${action}`
        setVoiceActionLoading(loadingKey)
        try {
            const response = await api.post(`/api/enterprises/${enterpriseId}/campaign/${campaign.id}/voice_${action}/`)
            if (response.status === 200) {
                updateVoiceCampaignRow(campaign.id, response.data?.ads_state, action)
                useNotification(
                    "Sucesso",
                    response.data?.message || "Campanha de voz atualizada.",
                    "success"
                )
                return
            }
            useNotification(
                "Ops!",
                response.data?.error || "Não foi possível atualizar a campanha de voz.",
                "danger"
            )
        } catch (error) {
            useNotification(
                "Ops!",
                error?.response?.data?.error || "Não foi possível comunicar com o ADS.",
                "danger"
            )
        } finally {
            setVoiceActionLoading(null)
        }
    }

    const updateVoiceCampaignRow = (campaignId, adsState, action) => {
        const fallbackStates = {
            start: 'RUNNING',
            pause: 'PAUSED',
            resume: 'RUNNING',
            finish: 'FINISHED',
        }
        const nextState = adsState || fallbackStates[action]
        if (!nextState)
            return

        updateCampaigns(prevState => prevState.map(campaign => {
            if (campaign.id !== campaignId)
                return campaign

            const isActive = ['STARTING', 'RUNNING', 'PAUSING', 'RESUMING', 'FINISHING'].includes(nextState)
            return {
                ...campaign,
                active: isActive,
                status: isActive ? 'PROCESSING' : 'IDLE',
                voice_config: {
                    ...(campaign.voice_config || {}),
                    ads_state: nextState,
                },
            }
        }))
    }

    const getVoiceState = (campaign) => {
        return campaign?.voice_config?.ads_state || "IDLE"
    }

    const renderVoiceActions = (campaign) => {
        if (campaign.platform !== 'VOICE')
            return null

        const state = getVoiceState(campaign)
        const isContinuous = campaign?.voice_config?.mode === 'CONTINUOUS'
        const isLoading = (action) => voiceActionLoading === `${campaign.id}-${action}`
        const anyLoading = Boolean(voiceActionLoading)

        return (
            <>
                {['', 'IDLE', 'ERROR'].includes(state) &&
                    <Tooltip title={isContinuous ? "Iniciar campanha" : "Iniciar discagem"} placement="top" disableInteractive>
                        <span>
                            <Button className="action-button"
                                disabled={anyLoading}
                                onClick={() => handleVoiceLifecycle(campaign, 'start')}>
                                <PlayArrowIcon />
                            </Button>
                        </span>
                    </Tooltip>
                }
                {state === 'RUNNING' &&
                    <Tooltip title={isContinuous ? "Pausar campanha" : "Pausar discagem"} placement="top" disableInteractive>
                        <span>
                            <Button className="action-button"
                                disabled={anyLoading || isLoading('pause')}
                                onClick={() => handleVoiceLifecycle(campaign, 'pause')}>
                                <PauseIcon />
                            </Button>
                        </span>
                    </Tooltip>
                }
                {state === 'PAUSED' &&
                    <Tooltip title={isContinuous ? "Retomar campanha" : "Retomar discagem"} placement="top" disableInteractive>
                        <span>
                            <Button className="action-button"
                                disabled={anyLoading || isLoading('resume')}
                                onClick={() => handleVoiceLifecycle(campaign, 'resume')}>
                                <ReplayIcon />
                            </Button>
                        </span>
                    </Tooltip>
                }
                {['RUNNING', 'PAUSED', 'ERROR'].includes(state) &&
                    <Tooltip title={isContinuous ? "Encerrar campanha" : "Encerrar discagem"} placement="top" disableInteractive>
                        <span>
                            <Button className="action-button delete"
                                disabled={anyLoading || isLoading('finish')}
                                onClick={() => handleVoiceLifecycle(campaign, 'finish')}>
                                <StopIcon />
                            </Button>
                        </span>
                    </Tooltip>
                }
            </>
        )
    }

    const deleteCampaign = async () => {
        updateReady(false)
        const url = `/api/enterprises/${enterpriseId}/campaign/${campaignData.id}/`;
        const response = await api.delete(url)

        if (response.status !== 204) {
            useNotification(
                'Ops!',
                'Você não tem permissão para realizar essa ação.',
                'danger'
            );
            updateReady(true)
            return
        }

        updateCampaigns(rmvObj(campaigns, [campaignData.id]))

        const currentLimit = (limitOffset.campaigns && limitOffset.campaigns[0]) || 25
        const currentOffset = (limitOffset.campaigns && limitOffset.campaigns[1]) || 0
        fetchCampaigns('auto', currentLimit, currentOffset)

        useNotification(
            'Sucesso',
            'Campanha removida com sucesso.',
            'success'
        )

        updateReady(true)
    }

    const submitCampaign = async () => {
        updateShowCampaignEdit(false)
        return (true)
    }

    // DEPRECATED
    // const resendNotifications = (notifications, campaignId, dateRange) => {
    //     if (notifications.length === 0) {
    //         useNotification(
    //             'Atenção',
    //             'Não há nenhuma notificação com falha que possa ser reenviada.',
    //             'warning'
    //         );
    //         return
    //     }
    //     api.post('api/notification/resend/', {ids: notifications})
    //     .then((res) => {
    //         if (res.status === 201) {
    //             setupNotification(campaignId, dateRange)
    //             return
    //         }
    //         throw error
    //     })
    //     .catch((err) => { console.error(err) })
    // }

    const setupNotification = async (campaignId, dateRange) => {
        const urlParams = new URLSearchParams()
        urlParams.set("selectedTab", "notifications")
        urlParams.set("campaignId", campaignId)
        urlParams.set("dateRange", dateRange)
        history.push(`${location.pathname}?${urlParams.toString()}`)
    }

    const CustomToolbar = () => {
        return (
            <GridToolbarContainer >
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
            </GridToolbarContainer>
        )
    }

    const table = useMemo(() => {

        const canCreateCampaigns = (permissions.isAdmin || permissions.isSupervisor) ? true : getSession().profile.change_campaigns


        const theme = createTheme({
            palette: {
                primary: { main: '#21446C' },
            },
        }, ptBR, corePtBR);

        const filterOperators = getGridStringOperators().filter(
            (operator) => ['contains', 'equals'].includes(operator.value)
        )
        const dateOperators = getGridDateOperators().filter(
            (operator) => ['after', 'before'].includes(operator.value)
        )

        const booleanOperators = getGridBooleanOperators()

        const choiceOperators = getGridSingleSelectOperators().filter(
            (operator) => ['is', 'not'].includes(operator.value)
        )

        const initializeColumns = () => {
            const thisColumns = (
                [
                    { field: 'name', headerName: 'Campanha', flex: 1, filterOperators: filterOperators },
                    { field: 'template', headerName: 'Template', flex: 1, hide: true, filterOperators: filterOperators },
                    { field: 'sender', headerName: 'Notificador', flex: 1, hide: true, filterOperators: filterOperators },
                    { field: 'contact_type', headerName: 'Filtro', flex: 1, filterOperators: filterOperators,
                        sortable: false,
                        renderCell: (params) => {
                            switch (params.value) {
                                case "selected":
                                    return "Selecionados"
                                case "imported":
                                    return "Importados via arquivo"
                                case "filtered":
                                    switch (params.row.contact_filter) {
                                        case "contact_group":
                                            return "Que fazem parte do(s) segmento(s)"
                                        case "contact_enterprise":
                                            return "Que fazem parte da(s) empresa(s)"
                                        case "contact_owner":
                                            return "Dono do contato"
                                        case "email":
                                            return `Que possuam e-mail com final ${params.row.contact_filter_value}`
                                        case "telephone":
                                            return `Que possuam telefone com DDD ${params.row.contact_filter_value}`
                                        case "created_gte":
                                            return `Com data de criação maior, ou igual a ${dayjs(new Date(params.row.contact_filter_value)).format("DD/MM/YYYY")}`
                                        case "created_lte":
                                            return `Com data de criação menor, ou igual a ${dayjs(new Date(params.row.contact_filter_value)).format("DD/MM/YYYY")}`
                                        case "birthday":
                                            return `Aniversariantes do mês de ${months[params.row.contact_filter_value]}`
                                    }
                                default:
                                    return ""
                            }
                        } 
                    },
                    {
                        field: 'groups', headerName: 'Segmentos', flex: 1, hide: isSmallScreen, filterOperators: filterOperators,
                        sortable: false,
                        renderCell: (params) => {
                            if (params.row.contact_filter === "contact_group" && params.value)
                                return params.value.map((group) => group.name).join(', ')
                            return ""
                        }
                    },
                    {
                        field: 'enterprises', headerName: 'Empresas', flex: 1, hide: isSmallScreen, filterOperators: filterOperators,
                        sortable: false,
                        renderCell: (params) => {
                            if (params.row.contact_filter === "contact_enterprise" && params.value) 
                                return params.value.map((enterprise) => enterprise.name).join(', ')
                            return ""
                        }
                    },
                    {
                        field: 'contacts', headerName: 'Contatos', flex: 1, hide: isSmallScreen, filterOperators: filterOperators,
                        sortable: false,
                        renderCell: ({ value }) => (value.length > 0 ? `${value.length} contato(s).` : "")
                    },
                    {
                        field: 'created_at', headerName: 'Data', flex: 1, hide: true, type: 'dateTime', filterOperators: dateOperators,
                        valueGetter: ({ value }) => {
                            if (value) {
                                const date = new Date(value);
                                date.setHours(date.getHours() - 3);
                                return date;
                            }
                        },
                        renderCell: (params) => (
                            <Box>
                                {params.value ? params.value.toLocaleString('pt-BR') : ''}
                            </Box>
                        )
                    },
                    {
                        field: 'next_execution', headerName: 'Próxima execução', flex: 1, type: 'dateTime', filterOperators: dateOperators,
                        valueGetter: ({ value }) => {
                            if (value) {
                                const date = new Date(value);
                                date.setHours(date.getHours() - 3);
                                return date;
                            }
                        },
                        renderCell: (params) => (
                            <Box>
                                {params.value ? params.value.toLocaleString('pt-BR') : ''}
                            </Box>
                        )
                    },
                    { field: 'queue_name', headerName: 'Fila', flex: 1, hide: true, filterOperators: filterOperators },
                    {
                        field: 'periodic', headerName: 'Periódico', flex: 1, hide: true, filterOperators: booleanOperators,
                        renderCell: (params) => {
                            if (params.value) {
                                return (<CheckCircleIcon className="icon-answered" />);
                            }
                            return (<CancelIcon className="icon-not-answered" />);
                        }
                    },
                    {
                        field: 'active', headerName: 'Status', flex: 1,
                        filterOperators: choiceOperators,
                        valueOptions: ["Agendado", "Edição", "Finalizado", "Processando", "Ativa"],
                        renderCell: (params) => {
                            if (params.row.platform === 'VOICE') {
                                const voiceState = getVoiceState(params.row)
                                const isContinuous = params.row?.voice_config?.mode === 'CONTINUOUS'
                                const labels = {
                                    IDLE: 'Pronta',
                                    STARTING: 'Iniciando...',
                                    RUNNING: isContinuous ? 'Ativa' : 'Discando',
                                    PAUSING: 'Pausando...',
                                    PAUSED: 'Pausada',
                                    RESUMING: 'Retomando...',
                                    FINISHING: 'Encerrando...',
                                    FINISHED: 'Encerrada',
                                    ERROR: 'Erro'
                                }
                                const colors = {
                                    IDLE: 'warning',
                                    STARTING: 'info',
                                    RUNNING: 'info',
                                    PAUSING: 'info',
                                    PAUSED: 'warning',
                                    RESUMING: 'info',
                                    FINISHING: 'info',
                                    FINISHED: 'success',
                                    ERROR: 'error'
                                }
                                return (
                                    <Chip
                                        label={labels[voiceState] || voiceState}
                                        color={colors[voiceState] || 'default'}
                                        size="small"
                                    />
                                )
                            }
                            if (params.row.status === "IDLE") {
                                return (
                                    <Chip
                                        label={!params.value ? "Edição" : params.row.next_execution ? "Agendado" : "Finalizado"}
                                        color={!params.value ? "warning" : params.row.next_execution ? "info" : "success"}
                                        size="small"
                                    />
                                )   
                            } else {
                                return (
                                    <Chip
                                        label={params.row.status === "FAILED" ? "Falhou" : "Processando..."}
                                        color={params.row.status === "FAILED" ? "error" : "info"}
                                        size="small"
                                    />
                                )
                            }
                        }
                    },
                    {
                        field: 'actions', headerName: 'Ações', flex: 1, hide: isSmallScreen, filterable: false, sortable: false,
                        renderCell: (params) => (
                            <Box>
                                {canCreateCampaigns && <>
                                    <Tooltip title="Editar campanha" placement="top" disableInteractive>
                                        <Button className="action-button"
                                            onClick={() => handleEdit(params.row)}>
                                            <EditIcon />
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title="Copiar campanha" placement="top" disableInteractive>
                                        <Button className="action-button"
                                            onClick={() => setDuplicate(params.row)}>
                                            <ContentCopyIcon />
                                        </Button>
                                    </Tooltip>
                                </>
                                }
                                <Tooltip title="Mais detalhes" placement="top" disableInteractive>
                                    <Button className="action-button"
                                        onClick={() => handleHistory(params.row)}>
                                        <HistoryIcon />
                                    </Button>
                                </Tooltip>
                                {params.row.platform !== 'VOICE' &&
                                    <Tooltip title="Ver disparos" placement="top" disableInteractive>
                                        <Button className="action-button"
                                            onClick={() => setupNotification(params.id, params.row.date_range)}>
                                            <MessageIcon />
                                        </Button>
                                    </Tooltip>
                                }
                                {canCreateCampaigns && renderVoiceActions(params.row)}
                                {canCreateCampaigns &&
                                    <Tooltip title="Excluir campanha" placement="top" disableInteractive>
                                        <Button className="action-button delete"
                                            onClick={() => handleDelete(params.row)}>
                                            <DeleteIcon />
                                        </Button>
                                    </Tooltip>}
                            </Box>
                        )
                    }
                ]
            )
            if (!canCreateCampaigns)
                delete thisColumns[9]

            return thisColumns
        }

        const getPage = (limit, offset, direction) => {
            if (direction == 'next')
                return Math.round(offset / limit) - 1
            return Math.round(offset / limit) + 1

        }
        const getQueryset = (string, search, delimiter) => {
            const queryset = string.split(search)[1]
            if (queryset) {
                const result = queryset.split(delimiter)
                return result ? result[0] : ''
            }
            return ''
        }
        const getLimitOffset = (url) => {
            const queryset = url.split('?').pop()
            limit = getQueryset(queryset, 'limit=', '&')
            offset = getQueryset(queryset, 'offset=', '&')
            if (pageLimit)
                limit = pageLimit
            return [limit, offset]
        }

        const handlePageChange = (event) => {
            if (event > page) {
                fetchCampaigns(...next)
                return
            }
            fetchCampaigns(...previous)
        }

        const handlePageSizeChange = (newPage) => {
            updatePageLimit(newPage)
            fetchCampaigns('auto', newPage, actual)
        }

        let count = 0
        let page = 0
        let next = ''
        let previous = ''
        let limit = 25
        let oldlimit = 0
        let offset = 0
        let actual = 0

        if (limitOffset.campaigns.length > 0) {
            limit = limitOffset.campaigns[0]
        }

        if (ServerData) {
            count = ServerData.campaigns.count
            previous = ServerData.campaigns.previous
            next = ServerData.campaigns.next

            if (next) {
                const limitOffset = getLimitOffset(next)
                next = ['auto', ...limitOffset]
                page = getPage(...limitOffset, 'next')
                offset = limitOffset.pop()
                oldlimit = limitOffset.pop()
                actual = Number(offset) - Number(limit)
                updateLimitOffset(prevState => ({ ...prevState, campaigns: [oldlimit, actual] }))
            }

            if (previous) {
                const limitOffset = getLimitOffset(previous)
                previous = ['auto', ...limitOffset]
                if (page == 0) {
                    page = getPage(...limitOffset, 'previous')
                    offset = limitOffset.pop()
                    oldlimit = limitOffset.pop()
                    actual = Number(offset) - Number(limit)
                    updateLimitOffset(prevState => ({ ...prevState, campaigns: [oldlimit, actual] }))
                }
            }
        }

        const handleFilterChange = event => {
            fetchCampaigns('filter', limit, actual, event)
            updateCampaignsFilter(event)
        }

        const handleSortChange = event => {
            fetchCampaigns('sort', limit, actual, { items: [] }, event)
            updateCampaignSortModel(event)
        }

        return (
            <>
                <Box sx={{ width: '100%', height: '100%' }}>
                    <ThemeProvider theme={theme}>
                        <DataGridPro
                            columns={initializeColumns()}
                            rows={campaigns}
                            disableSelectionOnClick
                            components={{ Toolbar: CustomToolbar }}
                            density={'compact'}
                            pagination
                            paginationMode="server"
                            rowsPerPageOptions={[25, 50, 100]}
                            page={page}
                            onPageSizeChange={handlePageSizeChange}
                            onPageChange={handlePageChange}
                            rowCount={count}
                            keepNonExistentRowsSelected
                            initialState={{
                                pagination: {
                                    pageSize: Number(limit)
                                }
                            }}

                            //filter
                            filterMode='server'
                            onFilterModelChange={handleFilterChange}
                            filterModel={campaignsFilter}

                            sortingMode="server"
                            sortModel={campaignSortModel}
                            onSortModelChange={handleSortChange}
                        />
                    </ThemeProvider>
                </Box>
            </>
        )

    }, [campaigns, columnVisibilityModel, campaignsFilter, voiceActionLoading])

    return (
        <>
            {table}
            {showCampaignEdit &&
                <CreateCampaignModal open={showCampaignEdit}
                    title="Nova campanha"
                    buttonText="Adicionar"
                    selectedKey={campaignData.id}
                    queues={queues}
                    senders={originNumbers}
                    templates={templates}
                    enterpriseId={enterpriseId}
                    handleClose={() => { }}
                    submit={submitCampaign}
                    selectedContacts={campaignData.contacts?.map((contact) => contact.contact)}
                />
            }
            <ConfirmationDialog open={showCampaignDelete}
                title="Confirmação"
                description={`Tem certeza que deseja excluir a campanha ${campaignData.name}?`}
                handleClose={() => { updateShowCampaignDelete(false) }}
                handleSubmit={deleteCampaign}
            />
            <CampaignHistory open={showCampaignHistory}
                title="Histórico da campanha"
                data={campaignData}
                enterpriseId={enterpriseId}
                buttonText="Fechar"
                handleClose={() => { updateShowCampaignHistory(false) }}
                setupNotification={setupNotification}
            />
            {duplicate && ("id" in duplicate) &&
                <Dialog open={duplicate} onClose={() => setDuplicate(null)}>
                    <DialogTitle>Confirmação</DialogTitle>
                    <Divider />
                    <DialogContent>
                        <DialogContentText>
                            <p style={{ marginBottom: "1rem" }}>
                                Você tem a opção de copiar a campanha completa (para todos os segmentos e/ou usuários),
                                ou somente incluindo os usuários que tiveram falha ou cancelamento no recebimento da mensagem.
                            </p>
                            <p>Como deseja copiar a campanha?</p>
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDuplicate(null)}>Cancelar</Button>
                        <Button onClick={() => handleDuplicate(true)}>Somente falhas/canceladas</Button>
                        <Button onClick={() => handleDuplicate()}>Completa</Button>
                    </DialogActions>
                </Dialog>
            }
        </>
    )
}

export default CampaignTable
